/**
 * SummaryAmortizationTab Component
 * Amortization schedule display within the Contract Summary step
 */

import React, { useState } from 'react';
import { Box, Alert } from '@mui/material';
import AmortizationScheduleTable from '../../../AmortizationScheduleTable';
import ScheduleEntryOverrideDialog from '../../../ScheduleEntryOverrideDialog';
import ScheduleOverridesModal from '../../../ScheduleOverridesModal';
import { usePermissions } from '../../../../../contexts/PermissionContext';
import { useNotification } from '../../../../../contexts/NotificationContext';
import { MODULES, PERMISSION_LEVELS } from '../../../../../contexts/PermissionConstants';
import { apiGet, apiPost } from '../../../../../utils/apiClient';
import logger from '../../../../../utils/logger';
import useServerPDF from '../../../../../hooks/useServerPDF';
import useConfirmDialog from '../../../../../hooks/useConfirmDialog';
import ConfirmDialog from '../../../../common/ConfirmDialog';
import { format } from 'date-fns';

function SummaryAmortizationTab({ contract, schedule, onRegenerate }) {
  const { hasPermission } = usePermissions();
  const notify = useNotification();
  const confirm = useConfirmDialog();
  const canAdmin = hasPermission(MODULES.CONTRACT_MANAGEMENT, PERMISSION_LEVELS.ADMIN);
  const { downloadPDF, isGenerating } = useServerPDF();
  const [overrideDialogOpen, setOverrideDialogOpen] = useState(false);
  const [selectedOverrideEntry, setSelectedOverrideEntry] = useState(null);
  const [overridesModalOpen, setOverridesModalOpen] = useState(false);
  const [overridesModalEntry, setOverridesModalEntry] = useState(null);

  const handleRegenerateSchedule = async () => {
    // The actual regeneration; runs directly when there are no applied overrides,
    // or from the confirm dialog's onConfirm when the user accepts discarding them.
    const doRegen = async () => {
      try {
        await apiPost(`/amortization/regenerate/${contract.id}`);
        if (onRegenerate) onRegenerate();
      } catch (err) {
        logger.error('Error regenerating schedule', { message: err.message });
        notify.error('Failed to regenerate schedule: ' + (err.response?.data?.message || err.message));
      }
    };

    try {
      const overridesResp = await apiGet(`/amendments/contract/${contract.id}/overrides`);
      const overrides = overridesResp?.data || overridesResp || [];
      const appliedCount = (Array.isArray(overrides) ? overrides : [])
        .filter(o => o.status === 'approved').length;
      if (appliedCount > 0) {
        confirm.show({
          title: 'Regenerate schedule?',
          message: `This contract has ${appliedCount} applied schedule override${appliedCount === 1 ? '' : 's'}. `
            + 'Regenerating will discard them. Continue?',
          confirmText: 'Regenerate',
          onConfirm: doRegen,
        });
        return;
      }
    } catch (overrideErr) {
      logger.warn('Could not check schedule overrides before regen', { message: overrideErr.message });
    }

    await doRegen();
  };

  const handleDownloadPDF = async () => {
    if (!contract?.id) return;
    try {
      // Server-side generation from the active 'amortization_schedule' template
      // (pdf_templates); the API rebuilds the schedule from the DB.
      const filename = `Amortization_Schedule_${contract.reference || contract.id}_${format(new Date(), 'yyyyMMdd')}.pdf`;
      await downloadPDF({ type: 'amortization', id: contract.id, filename });
    } catch (err) {
      logger.error('Error downloading amortization PDF', { message: err.message });
      notify.error('Failed to generate amortization schedule PDF: ' + (err.message || 'Please try again.'));
    }
  };

  if (!schedule || schedule.length === 0) {
    return (
      <Alert severity="info">
        No amortization schedule has been generated for this contract yet.
      </Alert>
    );
  }

  return (
    <Box>
      <AmortizationScheduleTable
        schedule={schedule}
        showAllRows
        contractId={contract.id}
        contract={contract}
        onRegenerate={handleRegenerateSchedule}
        hideRegenerateButton
        canAdmin={canAdmin}
        onDownloadPDF={handleDownloadPDF}
        isGeneratingPDF={isGenerating}
        onOverrideRequest={(entry) => {
          setSelectedOverrideEntry(entry);
          setOverrideDialogOpen(true);
        }}
        onShowOverrides={(entry) => {
          setOverridesModalEntry(entry);
          setOverridesModalOpen(true);
        }}
      />
      <ScheduleEntryOverrideDialog
        open={overrideDialogOpen}
        onClose={() => setOverrideDialogOpen(false)}
        contract={contract}
        scheduleEntry={selectedOverrideEntry}
        onSubmitSuccess={() => {
          setOverrideDialogOpen(false);
          if (onRegenerate) onRegenerate();
        }}
      />
      <ScheduleOverridesModal
        open={overridesModalOpen}
        onClose={() => {
          setOverridesModalOpen(false);
          setOverridesModalEntry(null);
        }}
        contract={contract}
        scheduleEntry={overridesModalEntry}
        onChanged={() => { if (onRegenerate) onRegenerate(); }}
      />
      <ConfirmDialog {...confirm.dialogProps} />
    </Box>
  );
}

export default SummaryAmortizationTab;
